/**
 * Shared constants between frontend and backend
 * This file ensures consistency across the application
 */

// Business/Event Categories
const BUSINESS_CATEGORIES = [
  { id: 'yoga', label: 'Yoga' },
  { id: 'restaurant', label: 'Restaurant' },
  { id: 'beauty', label: 'Beauty' },
  { id: 'fitness', label: 'Fitness' }
];

// Extract just the category IDs for validation
const VALID_CATEGORY_IDS = BUSINESS_CATEGORIES.map(cat => cat.id);

// Dress Codes
const DRESS_CODES = [
  'Casual',
  'Smart Casual',
  'Business Casual',
  'Business Formal',
  'Cocktail',
  'Black Tie',
  'White Tie',
  'Creative/Themed'
];

// Event Status Values
const EVENT_STATUS = {
  ACTIVE: 'active',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
  DRAFT: 'draft'
};

const VALID_EVENT_STATUSES = Object.values(EVENT_STATUS);

// User Status Values
const USER_STATUS = {
  PENDING: 'pending',
  ACTIVE: 'approved',  // Database uses 'approved' for active users
  BLOCKED: 'blocked',
  DELETED: 'deleted'
};

const VALID_USER_STATUSES = Object.values(USER_STATUS);

// Application Status Values
const APPLICATION_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled'
};

const VALID_APPLICATION_STATUSES = Object.values(APPLICATION_STATUS);

// User Type Values
const USER_TYPES = {
  INFLUENCER: 'influencer',
  BUSINESS: 'business',
  ADMIN: 'admin'
};

const VALID_USER_TYPES = Object.values(USER_TYPES);

// Field Names
const FIELD_NAMES = {
  // Common fields
  ID: 'id',
  EMAIL: 'email',
  PHONE_NUMBER: 'phoneNumber',
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
  STATUS: 'status',
  USER_TYPE: 'userType',
  DATA: 'data',
  
  // Personal info
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  BIRTHDAY: 'birthday',
  GENDER: 'gender',
  ALLERGIES: 'allergies',
  
  // Location
  COUNTRY: 'country',
  CITY: 'city',
  
  // Social media
  INSTAGRAM_HANDLE: 'instagramHandle',
  INSTAGRAM: 'instagram',
  TIKTOK_HANDLE: 'tiktokHandle',
  TIKTOK: 'tiktok',
  YOUTUBE_HANDLE: 'youtubeHandle',
  YOUTUBE: 'youtube',
  
  // Media
  PHOTOS: 'photos',
  
  // Business specific
  POSITION: 'position',
  BUSINESS_CATEGORY: 'businessCategory',
  BUSINESS_NAME: 'businessName',
  BUSINESS_ADDRESS: 'businessAddress',
  WEBSITE_URL: 'websiteUrl',
  
  // Event specific
  EVENT_NAME: 'eventName',
  EVENT_LOCATION: 'eventLocation',
  START_TIME: 'startTime',
  END_TIME: 'endTime',
  DRESS_CODE: 'dressCode',
  EVENT_DETAILS: 'eventDetails',
  EVENT_RULES: 'eventRules',
  PROPOSED_OFFER: 'proposedOffer',
  CATEGORY: 'category',
  BUSINESS_ID: 'businessId',
  
  // Application specific
  EVENT_ID: 'eventId',
  INFLUENCER_ID: 'influencerId',
  APPLICATION_ID: 'applicationId',
  
  // Subscription
  PAID_TILL_DATE: 'paidTillDate'
};

// Gender Options
const GENDER_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say'];

// Countries (can be expanded)
const COUNTRIES = [
  { id: 'latvia', label: 'Latvia' },
  { id: 'estonia', label: 'Estonia' },
  { id: 'lithuania', label: 'Lithuania' }
];

// Cities by country (can be expanded)
const CITIES_BY_COUNTRY = {
  latvia: [
    { id: 'riga', label: 'Riga' },
    { id: 'daugavpils', label: 'Daugavpils' },
    { id: 'liepaja', label: 'Liepāja' },
    { id: 'jelgava', label: 'Jelgava' }
  ],
  estonia: [
    { id: 'tallinn', label: 'Tallinn' },
    { id: 'tartu', label: 'Tartu' },
    { id: 'narva', label: 'Narva' }
  ],
  lithuania: [
    { id: 'vilnius', label: 'Vilnius' },
    { id: 'kaunas', label: 'Kaunas' },
    { id: 'klaipeda', label: 'Klaipėda' }
  ]
};

// Helper function to get category label from ID
function getCategoryLabel(categoryId) {
  const category = BUSINESS_CATEGORIES.find(cat => cat.id === categoryId);
  return category ? category.label : categoryId;
}

// Helper function to get category ID from label (case-insensitive)
function getCategoryId(categoryLabel) {
  if (!categoryLabel) return null;
  const category = BUSINESS_CATEGORIES.find(
    cat => cat.label.toLowerCase() === categoryLabel.toLowerCase()
  );
  return category ? category.id : categoryLabel.toLowerCase();
}

// Helper function to normalize category (convert label to ID if needed)
function normalizeCategory(category) {
  if (!category) return null;
  const categoryLower = category.toLowerCase();
  
  // Check if it's already a valid ID
  if (VALID_CATEGORY_IDS.includes(categoryLower)) {
    return categoryLower;
  }
  
  // Try to find by label
  const found = BUSINESS_CATEGORIES.find(
    cat => cat.label.toLowerCase() === categoryLower
  );
  
  return found ? found.id : categoryLower;
}

// Helper function to validate category
function isValidCategory(category) {
  if (!category) return false;
  return VALID_CATEGORY_IDS.includes(normalizeCategory(category));
}

// API Routes
// NOTE: These constants define all API endpoints used in the application
const API_ROUTES = {
  // Constants
  CONSTANTS: '/api/constants',
  
  // Authentication
  AUTH_CHECK_ACCOUNT: '/api/auth/check-account',
  AUTH_INFLUENCER_SIGNUP: '/api/auth/influencer/signup',
  AUTH_BUSINESS_SIGNUP: '/api/auth/business/signup',
  AUTH_FIRST_PASSWORD_SET: '/api/auth/first-password-set',
  
  // User Profile
  USER_PROFILE: '/api/user/profile',
  
  // Influencer
  INFLUENCER_PROFILE: '/api/influencer/profile',
  INFLUENCERS_BATCH: '/api/influencers/batch',
  
  // Business
  BUSINESS_PROFILE: '/api/business/profile',
  
  // Events
  EVENTS: '/api/events',
  EVENTS_BY_ID: '/api/events/:id',
  
  // Applications
  APPLICATIONS: '/api/applications',
  APPLICATIONS_BY_ID: '/api/applications/:id',

  // Event Views
  EVENT_VIEWS: '/api/event-views',
  
  // Admin
  ADMIN_PENDING_USERS: '/api/admin/pending-users',
  ADMIN_USER_BY_ID: '/api/admin/user/:id',
  ADMIN_APPROVE_USER: '/api/admin/approve-user',
  ADMIN_REJECT_USER: '/api/admin/reject-user',
  ADMIN_EMAILS: '/api/admin/admin-emails',
  ADMIN_SET_PAID_PERIOD: '/api/admin/set-paid-period',
  ADMIN_SEARCH_USER: '/api/admin/search-user'
};

// Helper function to build route with parameters
function buildRoute(route, params = {}) {
  let builtRoute = route;
  Object.keys(params).forEach(key => {
    builtRoute = builtRoute.replace(`:${key}`, params[key]);
  });
  return builtRoute;
}

// Export for CommonJS (Node.js backend)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    BUSINESS_CATEGORIES,
    VALID_CATEGORY_IDS,
    DRESS_CODES,
    EVENT_STATUS,
    VALID_EVENT_STATUSES,
    USER_STATUS,
    VALID_USER_STATUSES,
    USER_TYPES,
    VALID_USER_TYPES,
    APPLICATION_STATUS,
    VALID_APPLICATION_STATUSES,
    FIELD_NAMES,
    GENDER_OPTIONS,
    COUNTRIES,
    CITIES_BY_COUNTRY,
    API_ROUTES,
    getCategoryLabel,
    getCategoryId,
    normalizeCategory,
    isValidCategory,
    buildRoute
  };
}

// Export for ES6 modules (Frontend)
if (typeof exports !== 'undefined') {
  exports.BUSINESS_CATEGORIES = BUSINESS_CATEGORIES;
  exports.VALID_CATEGORY_IDS = VALID_CATEGORY_IDS;
  exports.DRESS_CODES = DRESS_CODES;
  exports.EVENT_STATUS = EVENT_STATUS;
  exports.VALID_EVENT_STATUSES = VALID_EVENT_STATUSES;
  exports.USER_STATUS = USER_STATUS;
  exports.VALID_USER_STATUSES = VALID_USER_STATUSES;
  exports.USER_TYPES = USER_TYPES;
  exports.VALID_USER_TYPES = VALID_USER_TYPES;
  exports.APPLICATION_STATUS = APPLICATION_STATUS;
  exports.VALID_APPLICATION_STATUSES = VALID_APPLICATION_STATUSES;
  exports.FIELD_NAMES = FIELD_NAMES;
  exports.GENDER_OPTIONS = GENDER_OPTIONS;
  exports.COUNTRIES = COUNTRIES;
  exports.CITIES_BY_COUNTRY = CITIES_BY_COUNTRY;
  exports.API_ROUTES = API_ROUTES;
  exports.getCategoryLabel = getCategoryLabel;
  exports.getCategoryId = getCategoryId;
  exports.normalizeCategory = normalizeCategory;
  exports.isValidCategory = isValidCategory;
  exports.buildRoute = buildRoute;
}

